/**
 * Paragraph Service
 *
 * Loads all document paragraphs with their style metadata and tracked-change
 * counts. Serves as the primary data source for paragraph navigation,
 * replacing the previous tracked-changes-only approach.
 */

/* global Word */

import { NOT_IN_PROFILE } from "src/features/review-slice";
import { buildStyleDefinitionMap } from "src/services";

import type { ParagraphInfo } from "src/types";

const STYLES_TO_INCLUDE = new Set(["ct", "cn", "pt"]);

/**
 * Loads every paragraph in the document, enriched with style-profile
 * metadata and per-paragraph tracked-change counts.
 *
 * @param profileName - Name of the style profile to use for lookups
 * @returns All paragraphs in document order
 */
export async function loadParagraphs(): Promise<Word.Paragraph[]> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load(["style", "uniqueLocalId"]);

    await context.sync();

    return paragraphs.items;
  });
}

export async function loadTrackedChanges(): Promise<Record<string, number>> {
  return Word.run(async (context) => {
    const trackedChanges = context.document.body.getTrackedChanges();
    trackedChanges.load("type");
    await context.sync();

    const filteredChanges = trackedChanges.items.filter(
      (change) =>
        change.type !== Word.TrackedChangeType.added &&
        change.type !== Word.TrackedChangeType.deleted
    );

    const paragraphCollections = filteredChanges.map((change) => {
      const paragraphs = change.getRange().paragraphs;
      paragraphs.load("uniqueLocalId");
      return paragraphs;
    });

    await context.sync();

    return paragraphCollections
      .flatMap((paragraphCollection) => paragraphCollection.items)
      .reduce<Record<string, number>>((acc, item) => {
        acc[item.uniqueLocalId] = 1;
        return acc;
      }, {});
  });
}

/**
 * Gets the current style and category for a single paragraph.
 *
 * Used after reject to detect if the paragraph's style changed
 * without reloading the entire document.
 *
 * @param uniqueLocalId - The paragraph's session-scoped unique ID
 * @param profileName - Name of the style profile to use for category lookup
 * @returns The paragraph's current style and category
 */
export async function getParagraphStyleInfo(
  uniqueLocalId: string,
  profileName: string
): Promise<{ style: string; category: string; fullStyleName: string }> {
  const styleProfileMap = await buildStyleDefinitionMap(profileName);

  try {
    return Word.run(async (context) => {
      const paragraph = context.document.getParagraphByUniqueLocalId(uniqueLocalId);
      paragraph.load("style");
      await context.sync();

      const styleDef = styleProfileMap.get(paragraph.style);
      return {
        style: paragraph.style,
        category: styleDef?.category ?? NOT_IN_PROFILE,
        fullStyleName: styleDef?.full_name ?? paragraph.style,
      };
    });
  } catch (err) {
    console.error(`[getParagraphStyleInfo] ERROR - uniqueLocalId=${uniqueLocalId}`, err);
    throw err;
  }
}

/**
 * Gets the current text content for a single paragraph.
 *
 * @param uniqueLocalId - The paragraph's session-scoped unique ID
 * @returns The paragraph's current text content
 */
export async function getParagraphTextByUniqueLocalId(uniqueLocalId: string): Promise<string> {
  try {
    return Word.run(async (context) => {
      const paragraph = context.document.getParagraphByUniqueLocalId(uniqueLocalId);
      paragraph.load("text");
      await context.sync();

      return paragraph.text;
    });
  } catch (err) {
    console.error(`[getParagraphTextByUniqueLocalId] ERROR - uniqueLocalId=${uniqueLocalId}`, err);
    throw err;
  }
}

/**
 * Builds a uniqueLocalId -> paragraph text lookup for selected style codes.
 *
 * Includes only paragraphs whose current style is ct, cn, or pt.
 *
 * @param paragraphs - Paragraph metadata to filter and resolve
 * @returns Lookup keyed by uniqueLocalId with paragraph text values
 */
export async function getDivisionText(
  paragraphs: ParagraphInfo[]
): Promise<Record<string, string>> {
  const targetParagraphs = paragraphs.filter((paragraph) =>
    STYLES_TO_INCLUDE.has(paragraph.currentStyle)
  );

  if (targetParagraphs.length === 0) {
    return {};
  }

  return Word.run(async (context) => {
    const paragraphs = targetParagraphs.map((p) => {
      const paragraph = context.document.getParagraphByUniqueLocalId(p.uniqueLocalId);
      paragraph.load(["text", "uniqueLocalId"]);
      return paragraph;
    });

    await context.sync();

    return paragraphs.reduce<Record<string, string>>((acc, p) => {
      acc[p.uniqueLocalId] = p.text;
      return acc;
    }, {});
  });
}
